import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash, FolderOpen, Plus, ArrowRight, ArrowsHorizontal } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { fmtCurrency } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Scenarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/scenarios`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load scenarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await axios.delete(`${API}/scenarios/${id}`);
      toast.success("Scenario deleted");
      setItems((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <main data-testid="scenarios-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <span className="label-eyebrow">Saved Breakdowns</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            Your client scenarios
          </h1>
          <p className="mt-3 text-brand-ink/70 max-w-xl">
            Every breakdown you've built lives here — open one at any time before a meeting.
          </p>
        </div>
        <Link
          to="/calculator"
          data-testid="new-breakdown-btn"
          className="inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-5 py-3 text-sm font-semibold transition-colors"
        >
          <Plus size={16} weight="bold" /> New breakdown
        </Link>
        {items.length >= 2 && (
          <Link
            to="/scenarios/compare"
            data-testid="compare-btn"
            className="inline-flex items-center gap-2 rounded-md border border-brand-copper/40 bg-brand-copper/10 hover:bg-brand-copper/15 text-brand-copper px-5 py-3 text-sm font-semibold transition-colors"
          >
            <ArrowsHorizontal size={16} weight="bold" /> Compare scenarios
          </Link>
        )}
      </div>

      {renderScenariosBody({ loading, items, handleDelete })}
    </main>
  );
}

function renderScenariosBody({ loading, items, handleDelete }) {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-brand-line rounded-md p-6 h-40 animate-pulse" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        data-testid="scenarios-empty"
        className="bg-white border border-brand-line border-dashed rounded-md p-14 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-brand-cream flex items-center justify-center text-brand-forest">
          <FolderOpen size={22} weight="duotone" />
        </div>
        <h3 className="mt-5 font-serif text-2xl text-brand-forest">No scenarios yet</h3>
        <p className="mt-3 text-sm text-brand-ink/60 max-w-xs mx-auto">
          Run a breakdown and hit <span className="font-semibold">Save scenario</span> to see it land here.
        </p>
        <Link
          to="/calculator"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-5 py-3 text-sm font-semibold"
        >
          Open calculator <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((s) => (
        <div
          key={s.id}
          data-testid={`scenario-card-${s.id}`}
          className="group bg-white border border-brand-line rounded-md p-6 flex flex-col justify-between transition-all hover:-translate-y-1 hover:shadow-lg"
        >
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow">{s.business_name || "Client"}</div>
                <h3 className="mt-2 font-serif text-2xl text-brand-forest leading-tight">
                  {s.client_name}
                </h3>
              </div>
              <button
                data-testid={`delete-scenario-${s.id}`}
                onClick={() => handleDelete(s.id)}
                className="text-brand-muted hover:text-red-700 transition-colors"
                title="Delete"
              >
                <Trash size={18} weight="bold" />
              </button>
            </div>
            <div className="mt-6 pt-5 border-t border-brand-line">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">
                {s.horizon_years}-year projected gain
              </div>
              <div className="mt-1.5 font-serif text-4xl text-brand-copper">
                {fmtCurrency(s.headline_wealth_gain, { compact: false })}
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <span className="text-xs text-brand-muted">
              {new Date(s.created_at).toLocaleDateString("en-CA", {
                year: "numeric", month: "short", day: "numeric",
              })}
            </span>
            <Link
              to={`/scenarios/${s.id}`}
              data-testid={`open-scenario-${s.id}`}
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-forest hover:text-brand-copper transition-colors"
            >
              Open <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
